import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Controller, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import Select from "react-select";

import { faWheatAwn } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TextInput, Input } from "@wfp/ui";

import QuantityInput from "components/QuantityInput";
import CurrencyInput from "components/CurrencyInput";

import RepeaterAddButton from "components/RepeaterAddButton";
import Empty from "components/Empty";
import CommoditySelect from "components/CommoditySelect";
import { Repeater, RepeaterItem } from "components/Repeater";
import CommoditiesAddButton from "components/RepeaterAddButton";
import Camera from "components/Camera";

import {
  getPurchaseOtherCostsList,
  requestPurchaseOtherCosts,
} from "data-handler/ducks/purchaseOtherCosts";

import { getIsOnlineMode } from "data-handler/ducks/auth";

import { purchaseDetailCategory } from "SCConstants";
import "./_cash-to-school.scss";
import { StockStatus } from "helpers/stock";
import { PurchaseEvent } from "data-handler/ducks/stores";
import { getCurrencyForCurrentSchool } from "data-handler/ducks/currency";

function otherCosts(list: { id: string; name: string }[]) {
  return list.map((e) => {
    return {
      value: e.id,
      label: e.name,
    };
  });
}

function calculateTotal(list: { total_paid: string }[] = []) {
  return list.reduce((acc, cur) => {
    return parseFloat(cur?.total_paid || "0") + acc;
  }, 0);
}

function updateGrandTotal(
  getValues: () => { stock_events: any; other_costs: any },
  setValue: (name: string, value: string) => void,
  decimalPlaces: number = 0
) {
  const values = getValues();
  const commodities = values["stock_events"]; // List
  const otherCosts = values["other_costs"]; // list
  const commodityTotal = calculateTotal(commodities);
  const otherCostsTotal = calculateTotal(otherCosts);
  const total =
    (isNaN(commodityTotal) ? 0 : commodityTotal) +
    (isNaN(otherCostsTotal) ? 0 : otherCostsTotal);
  const formattedTotal = total.toFixed(decimalPlaces);
  setValue(`total_paid`, formattedTotal);
}

type FormErrors = {
  [key: string]: { message: JSX.Element; requiredMessage?: JSX.Element };
};

type CommoditiesEditProps = {
  errors: FormErrors;
  control: any;
  setValue: any;
  watch: any;
  getValues: any;
  register: any;
  purchaseCurrentStoreData: PurchaseEvent;
  stockStatus: StockStatus;
};

const CommoditiesEdit: React.ElementType<CommoditiesEditProps> = ({
  errors,
  control,
  setValue,
  watch,
  getValues,
  register,
  purchaseCurrentStoreData,
  stockStatus,
}) => {
  const purchaseOtherCosts = useSelector(getPurchaseOtherCostsList);
  const isOnlineMode = useSelector(getIsOnlineMode);
  const defaultCurrency = useSelector(getCurrencyForCurrentSchool);

  const [refreshKey, setRefreshKey] = useState(new Date().valueOf());
  const dispatch = useDispatch();
  useEffect(() => {
    if (
      isOnlineMode &&
      purchaseOtherCosts &&
      purchaseOtherCosts.ids === undefined
    ) {
      dispatch(requestPurchaseOtherCosts());
    }
  }, [dispatch, isOnlineMode, purchaseOtherCosts]);

  // List of purchase detail commodities that cannot be deleted because if we delete them,
  // if we delete them, final quantity will be negative. They are used somewhere.
  const undeletablePurchaseDetailCommodityIds: Set<number> = useMemo(() => {
    const result: Set<number> = new Set();
    const originalCommodities = (
      purchaseCurrentStoreData?.stock_events || []
    ).filter(
      (purchaseCommodity: any) =>
        purchaseCommodity.category === purchaseDetailCategory
    );

    for (const commodity of originalCommodities) {
      const commodityId = commodity.id;
      if (commodityId === undefined) continue;
      const finalQuantity = Number(
        stockStatus.purchasedetail[commodityId] || "0"
      );
      const purchaseQuantity = Number(commodity.quantity);
      // If purchaseQuantity is larger than what we have right now,
      // that means some of the quantity is used. So we should not be able to
      // delete this commodity from delivery. If we delete it, final quantity will be
      // negative.
      if (purchaseQuantity > finalQuantity) {
        result.add(commodityId);
      }
    }
    return result;
  }, [purchaseCurrentStoreData, stockStatus.purchasedetail]);

  var total = watch(`total_paid`);

  useEffect(() => {
    setRefreshKey(new Date().valueOf());
    setValue(`total_paid`, total);
  }, [setValue, total]);

  const purchaseOtherCostsOptions = purchaseOtherCosts
    ? otherCosts(Object.values(purchaseOtherCosts?.results))
    : undefined;
  const maxCommentCharacters = 200;

  const requiredLabelCommodities = (
    <div className="wfp--form-required">
      <FormattedMessage
        id="PurchaseEdit.commodityError"
        defaultMessage=" Select commodity"
      />
    </div>
  );

  let {
    fields: commodityRows,
    append: appendCommodity,
    remove: removeCommodity,
  } = useFieldArray({
    control,
    name: "stock_events",
  });

  let {
    fields: purchaseRows,
    append: appendOtherCosts,
    remove: removeOtherCosts,
  } = useFieldArray({
    control,
    name: "other_costs",
  });

  useEffect(
    () => updateGrandTotal(getValues, setValue, defaultCurrency.decimals),
    [
      getValues,
      setValue,
      commodityRows.length,
      purchaseRows.length,
      defaultCurrency.decimals,
    ]
  );

  const customStyles = (invalid: boolean) => ({
    control: (styles: any) =>
      invalid === false || invalid === undefined
        ? { ...styles, border: "1px solid #8ca4b5" }
        : { ...styles, borderColor: "#C5192D !important" },
  });

  return (
    <div className="commodities-section">
      <div className="wfp--form-item wfp--form-item--invalid commodity-select">
        {commodityRows.length === 0 && (
          <Empty
            title="No commodities added yet"
            icon={<FontAwesomeIcon icon={faWheatAwn} size="1x" />}
          >
            <FormattedMessage
              id="PurchaseEdit.addACommodityForThisDelivery"
              defaultMessage="Add a commodity for this purchase"
            />
          </Empty>
        )}
        {commodityRows.length === 0 && (
          <Empty
            title="No commodities added yet"
            icon={<FontAwesomeIcon icon={faWheatAwn} size="1x" />}
          >
            <FormattedMessage
              id="PurchaseEdit.addCommodityForThisDelivery"
              defaultMessage="Add a commodity for this purchase"
            />
          </Empty>
        )}
        <Repeater>
          {commodityRows.map((commodityRow, index) => (
            <RepeaterItem
              key={commodityRow.id}
              removeAction={() => removeCommodity(index)}
              disableRemove={
                commodityRows.length === 1 ||
                undeletablePurchaseDetailCommodityIds.has(
                  Number(commodityRow.id)
                )
              }
              className="purchase-edit__commodities"
            >
              <Controller
                control={control}
                render={({ onChange, value }) => (
                  <Camera onChange={onChange} image={value} />
                )}
                name={`stock_events[${index}].picture`}
              />
              <div className="container">
                <Controller
                  as={<input type="hidden" />}
                  name={`stock_events[${index}].id`}
                />
                <Controller
                  render={({ onChange, value }) => {
                    return (
                      <CommoditySelect
                        className="wfp--react-select-container"
                        required
                        category={purchaseDetailCategory}
                        key={`stock_events[${index}].commodity`}
                        onChange={(e) => {
                          setValue(
                            `stock_events[${index}].measure_unit`,
                            e.measure_unit
                          );
                          onChange(e.value);
                        }}
                        value={value}
                        inputRef={register}
                        invalidOverride={
                          errors[`stock_events[${index}].commodity`] !==
                          undefined
                        }
                        invalidTextOverride={requiredLabelCommodities}
                      />
                    );
                  }}
                  name={`stock_events[${index}].commodity`}
                  control={control}
                />

                <Controller
                  render={({ onChange, onBlur, value }) => {
                    return (
                      <QuantityInput
                        control={control}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        labelText={
                          <FormattedMessage
                            id="PurchaseEdit.quantity"
                            defaultMessage="Quantity"
                          />
                        }
                        setValue={setValue}
                        name={`stock_events[${index}].quantity`}
                        invalid={!!errors[`stock_events[${index}].quantity`]}
                        invalidText={
                          errors[`stock_events[${index}].quantity`]?.message
                        }
                        measureUnit={watch(
                          `stock_events[${index}].measure_unit`
                        )}
                      />
                    );
                  }}
                  control={control}
                  name={`stock_events[${index}].quantity`}
                />

                <Controller
                  render={({ value }) => {
                    return (
                      <TextInput
                        labelText={
                          <FormattedMessage
                            id="PurchaseEdit.unit"
                            defaultMessage="Unit"
                          />
                        }
                        value={value?.name ?? "Kilogram"}
                        disabled
                      ></TextInput>
                    );
                  }}
                  invalid={errors[`stock_events[${index}].measure_unit`]}
                  invalidText={
                    errors[`stock_events[${index}].measure_unit`]?.message
                  }
                  name={`stock_events[${index}].measure_unit`}
                  control={control}
                />

                <Controller
                  render={({ onChange, value }) => (
                    <CurrencyInput
                      value={value}
                      onChange={(onChangeValue) => {
                        onChange(onChangeValue ? onChangeValue : 1);
                        updateGrandTotal(
                          getValues,
                          setValue,
                          defaultCurrency.decimals
                        );
                      }}
                      labelText={
                        <FormattedMessage
                          id="PurchaseEdit.totalPaid"
                          defaultMessage="Total amount paid"
                        />
                      }
                      setValue={setValue}
                      control={control}
                      name={`stock_events[${index}].total_paid`}
                      id={`stock_events[${index}].total_paid`}
                      invalid={!!errors[`stock_events[${index}].total_paid`]}
                      invalidText={
                        errors[`stock_events[${index}].total_paid`]?.message
                      }
                    />
                  )}
                  control={control}
                  name={`stock_events[${index}].total_paid`}
                />
              </div>
              <Controller
                as={
                  <TextInput
                    maxLength={maxCommentCharacters}
                    labelText={
                      <>
                        <FormattedMessage
                          id="PurchaseEdit.comments"
                          defaultMessage="comments"
                        />{" "}
                        <FormattedMessage
                          id="Form.optional"
                          defaultMessage="(optional)"
                        />
                      </>
                    }
                  />
                }
                name={`stock_events[${index}].comments`}
              />
            </RepeaterItem>
          ))}
        </Repeater>
        <CommoditiesAddButton
          onClick={() => {
            appendCommodity({
              issues: [],
            });
          }}
        />{" "}
      </div>
      <div className="wfp--form-item wfp--form-item--invalid other-costs">
        <Repeater>
          {purchaseRows.map((purchaseRow, index) => (
            <RepeaterItem
              key={purchaseRow.id}
              removeAction={() => removeOtherCosts(index)}
              className="purchase-edit__commodities"
            >
              <div className="container">
                <Controller
                  render={({ onChange, value }) => {
                    return (
                      <Input
                        labelText={
                          <FormattedMessage
                            id="PurchaseEdit.otherCost"
                            defaultMessage="Other cost"
                          />
                        }
                        invalid={!!errors[`other_costs[${index}].other_cost`]}
                        invalidText={
                          errors[`other_costs[${index}].other_cost`]?.message
                        }
                      >
                        {() => (
                          <Select
                            className="wfp--react-select-container"
                            classNamePrefix="wfp--react-select"
                            options={purchaseOtherCostsOptions}
                            defaultValue={value}
                            placeholder="Select ..."
                            control={control}
                            onChange={(e: any) => {
                              onChange(e.value);
                            }}
                            styles={customStyles(
                              !!errors[`other_costs[${index}].other_cost`]
                            )}
                          />
                        )}
                      </Input>
                    );
                  }}
                  name={`other_costs[${index}].other_cost`}
                />
                <Controller
                  className="purchaseEdit-total"
                  render={({ onChange, value }) => (
                    <CurrencyInput
                      value={value}
                      onChange={(onChangeValue) => {
                        onChange(onChangeValue ? onChangeValue : 1);
                        updateGrandTotal(
                          getValues,
                          setValue,
                          defaultCurrency.decimals
                        );
                      }}
                      labelText={
                        <FormattedMessage
                          id="PurchaseEdit.totalPaid"
                          defaultMessage="Total amount paid"
                        />
                      }
                      control={control}
                      setValue={setValue}
                      name={`other_costs[${index}].total_paid`}
                      id={`other_costs[${index}].total_paid`}
                      invalid={!!errors[`other_costs[${index}].total_paid`]}
                      invalidText={
                        errors[`other_costs[${index}].total_paid`]?.message
                      }
                    />
                  )}
                  name={`other_costs[${index}].total_paid`}
                />
              </div>
              <Controller
                as={
                  <TextInput
                    maxLength={maxCommentCharacters}
                    labelText={
                      <>
                        <FormattedMessage
                          id="PurchaseEdit.comments"
                          defaultMessage="comments"
                        />{" "}
                        <FormattedMessage
                          id="Form.optional"
                          defaultMessage="(optional)"
                        />
                      </>
                    }
                  />
                }
                name={`other_costs[${index}].comments`}
              />
            </RepeaterItem>
          ))}
        </Repeater>
        <RepeaterAddButton
          text={
            <FormattedMessage
              id="PurchaseEdit.addOther"
              defaultMessage="Add other cost"
            />
          }
          onClick={() => {
            appendOtherCosts({});
          }}
        />
      </div>
      <div className="grand-total">
        <Controller
          render={({ value, onChange }) => (
            <CurrencyInput
              defaultValue={watch(`total_paid`)}
              value={watch(`total_paid`)}
              key={refreshKey}
              control={control}
              setValue={setValue}
              labelText={
                <FormattedMessage
                  id="PurchaseEdit.grandTotal"
                  defaultMessage="Grand total"
                />
              }
              name={"total_paid"}
              id={"total_paid"}
              invalid={!!errors?.total_paid}
              invalidText={errors?.total_paid?.message}
            />
          )}
          readonly
          name={`total_paid`}
        />
      </div>
    </div>
  );
};

export default CommoditiesEdit;
